export default {
  title: 'Privacy Policy',
  lastUpdated: 'Last Updated: November 27, 2025',
  section1: {
    title: '1. Introduction',
    content: 'Welcome to UmiGo. We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you use our mobile application and website, and tell you about your privacy rights and how the law protects you.'
  },
  section2: {
    title: '2. Information We Collect',
    intro: 'We collect and process the following types of information:',
    items: [
      '<strong>Account Information:</strong> Username, email address, phone number, date of birth, gender, and profile photo',
      '<strong>Profile Data:</strong> Bio, interests, preferences, and settings you configure',
      '<strong>Content:</strong> Messages, photos, videos, voice recordings, and other content you create or share',
      '<strong>Usage Data:</strong> Information about how you use our services, including features accessed, time spent, and interactions',
      '<strong>Device Information:</strong> Device type, operating system, unique device identifiers, mobile network information',
      '<strong>Location Data:</strong> Approximate location based on IP address or precise location if you grant permission',
      '<strong>Payment Information:</strong> Payment card details and transaction history for in-app purchases',
      '<strong>Communications:</strong> Customer support interactions and feedback you provide'
    ]
  },
  section3: {
    title: '3. How We Use Your Information',
    intro: 'We use your information to:',
    items: [
      'Provide, maintain, and improve our services',
      'Create and manage your account',
      'Enable communication between users through messaging, voice, and video features',
      'Personalize your experience and provide content recommendations',
      'Process transactions and send related information',
      'Send you technical notices, updates, security alerts, and support messages',
      'Respond to your comments, questions, and customer service requests',
      'Monitor and analyze trends, usage, and activities',
      'Detect, prevent, and address technical issues, fraud, and illegal activities',
      'Comply with legal obligations and enforce our terms'
    ]
  },
  section4: {
    title: '4. Information Sharing and Disclosure',
    intro: 'We may share your information in the following circumstances:',
    items: [
      '<strong>With Other Users:</strong> Your profile information and content you share are visible to other users according to your privacy settings',
      '<strong>Service Providers:</strong> Third-party companies that help us operate our services (hosting, analytics, payment processing, customer support)',
      '<strong>Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition',
      '<strong>Legal Requirements:</strong> When required by law or to protect rights, property, or safety',
      '<strong>With Your Consent:</strong> When you explicitly agree to share information with third parties'
    ],
    note: 'We do not sell your personal information to third parties for their marketing purposes.'
  },
  section5: {
    title: '5. Data Security',
    intro: 'We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. This includes:',
    items: [
      'End-to-end encryption for private messages',
      'Secure data transmission using SSL/TLS protocols',
      'Regular security assessments and updates',
      'Access controls and authentication mechanisms',
      'Employee training on data protection practices'
    ]
  },
  section6: {
    title: '6. Data Retention',
    content: 'We retain your personal data only for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required by law. When you delete your account, we will delete or anonymize your personal data within 90 days, except where we need to retain it for legal compliance or legitimate business purposes.'
  },
  section7: {
    title: '7. Your Privacy Rights',
    intro: 'Depending on your location, you may have the following rights:',
    items: [
      '<strong>Access:</strong> Request access to your personal data',
      '<strong>Correction:</strong> Request correction of inaccurate or incomplete data',
      '<strong>Deletion:</strong> Request deletion of your personal data',
      '<strong>Portability:</strong> Request transfer of your data to another service',
      '<strong>Objection:</strong> Object to processing of your personal data',
      '<strong>Restriction:</strong> Request restriction of processing',
      '<strong>Withdraw Consent:</strong> Withdraw consent at any time where we rely on consent'
    ],
    note: 'To exercise these rights, please contact us at privacy@umigo.cc'
  },
  section8: {
    title: '8. Children\'s Privacy',
    content: 'UmiGo is not intended for users under the age of 13 (or 16 in certain jurisdictions). We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal data, we will take steps to delete such information. If you believe we have collected information from a child, please contact us immediately.'
  },
  section9: {
    title: '9. International Data Transfers',
    content: 'Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. We ensure appropriate safeguards are in place to protect your personal data in accordance with this privacy policy.'
  },
  section10: {
    title: '10. Cookies and Tracking Technologies',
    content: 'We use cookies and similar tracking technologies to collect and track information about your use of our services. You can control cookies through your browser settings, but disabling cookies may affect your ability to use certain features.'
  },
  section11: {
    title: '11. Third-Party Links and Services',
    content: 'Our services may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies before providing any information.'
  },
  section12: {
    title: '12. Changes to This Privacy Policy',
    content: 'We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last Updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.'
  },
  section13: {
    title: '13. Contact Us',
    intro: 'If you have any questions about this privacy policy or our privacy practices, please contact us:',
    email: '<strong>Email:</strong> privacy@umigo.cc',
    phone: '<strong>Phone:</strong> WA: +52 8 132724843',
    address: '<strong>Address:</strong> Av. Paseo de la Reforma 222, Ciudad de México, CDMX 06600, Mexico'
  },
  regional: {
    title: 'Regional Privacy Information',
    ccpa: {
      title: 'For California Residents (CCPA)',
      intro: 'California residents have additional rights under the California Consumer Privacy Act (CCPA):',
      items: [
        'Right to know what personal information is collected, used, shared, or sold',
        'Right to delete personal information',
        'Right to opt-out of the sale of personal information',
        'Right to non-discrimination for exercising CCPA rights'
      ]
    },
    gdpr: {
      title: 'For European Users (GDPR)',
      content: 'If you are located in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR), including the right to access, rectify, erase, restrict processing, data portability, and to object to processing of your personal data.'
    }
  }
};
